import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { FilePlus, FileText, Repeat, Trash2 } from 'lucide-react';
import type { Todo } from '../api/types'
import { ContextMenu, useContextMenu } from './primitives/ContextMenu'
import { MoveToMenu } from './MoveToMenu'
import { PriorityPicker } from './PriorityPicker'
import { RecurrencePicker } from './RecurrencePicker'
import { ReminderPicker } from './ReminderPicker'
import { useTodoActions } from '../hooks/useTodoActions'
import { useDeleteRecurrence } from '../api/hooks'

interface TodoItemProps {
  todo: Todo
  /**
   * Pass `isOverlay` when rendering inside DragOverlay (the floating copy).
   * The overlay renders at full opacity with a subtle shadow.
   * The actual source item dims via useSortable's isDragging flag instead.
   */
  isOverlay?: boolean
}

export function TodoItem({ todo, isOverlay = false }: TodoItemProps) {
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(todo.text);
  const inputRef = useRef<HTMLInputElement>(null)

  const actions = useTodoActions(todo)
  const contextMenu = useContextMenu()
  const deleteRecurrence = useDeleteRecurrence()
  const [recurrenceOpen, setRecurrenceOpen] = useState(false)

  const isRecurring = !!todo.recurrence_id

  const contextMenuSections = useMemo(() => {
    const sections: Parameters<typeof ContextMenu>[0]['items'] = [
      {
        type: 'custom' as const,
        render: () => (
          <PriorityPicker
            todoId={todo.id}
            currentPriority={todo.priority}
            onApplied={contextMenu.closeMenu}
          />
        ),
      },
      {
        type: 'custom' as const,
        render: () => <MoveToMenu todo={todo} onMoved={contextMenu.closeMenu} />,
      },
    ]
    if (!isRecurring) {
      sections.push({
        type: 'actions' as const,
        actions: [
          {
            id: 'make-recurring',
            label: 'Make recurring…',
            icon: <Repeat size={14} strokeWidth={1.5} />,
            onClick: () => {
              setRecurrenceOpen(true)
              contextMenu.closeMenu()
            },
          },
        ],
      })
    } else if (todo.recurrence_id) {
      sections.push({
        type: 'actions' as const,
        actions: [
          {
            id: 'delete-series',
            label: 'Delete entire series',
            icon: <Trash2 size={14} strokeWidth={1.5} />,
            onClick: () => {
              const confirmed = window.confirm(
                `Stop the recurring series "${todo.text}"?\n\nThis removes all future occurrences. Past completions stay in place.`,
              )
              if (confirmed && todo.recurrence_id) {
                deleteRecurrence.mutate(todo.recurrence_id)
              }
              contextMenu.closeMenu()
            },
          },
        ],
      })
    }
    return sections
  }, [todo, todo.id, todo.text, todo.priority, todo.recurrence_id, contextMenu.closeMenu, isRecurring, deleteRecurrence])

  const { attributes, listeners, setNodeRef, transform, transition, isDragging: sortableDragging } =
    useSortable({ id: todo.id })

  const wasDragging = useRef(false)
  const [suppressTransform, setSuppressTransform] = useState(false)

  useEffect(() => {
    if (wasDragging.current && !sortableDragging) {
      setSuppressTransform(true)
      const id = requestAnimationFrame(() => setSuppressTransform(false))
      return () => cancelAnimationFrame(id)
    }
    wasDragging.current = sortableDragging
  }, [sortableDragging])

  const handleStatusClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      actions.toggleStatus()
    },
    [actions]
  )

  const handleTextClick = useCallback(() => {
    if (!editing) {
      setEditing(true)
      setEditText(todo.text)
      setTimeout(() => inputRef.current?.select(), 0)
    }
  }, [editing, todo.text])

  const handleEditSave = useCallback(() => {
    actions.saveEditedText(editText)
    setEditing(false)
  }, [editText, actions])

  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleEditSave()
      } else if (e.key === 'Escape') {
        setEditing(false)
        setEditText(todo.text)
      }
    },
    [handleEditSave, todo.text]
  )

  const handleOpenNoteDrawer = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      actions.openNote();
    },
    [actions],
  );

  const handleDeleteClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      actions.deleteImmediate()
    },
    [actions]
  )

  const handleDeleteKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Backspace' && !editing) {
        const confirmed = window.confirm(`Delete "${todo.text}"?`)
        if (confirmed) {
          actions.deleteImmediate()
        }
      }
    },
    [editing, todo.text, actions]
  )

  const style: React.CSSProperties = isOverlay
    ? {
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        background: 'var(--color-paper)',
        borderRadius: 'var(--radius-sm)',
        opacity: 1,
      }
    : suppressTransform
    ? {
        transform: undefined,
        transition: 'none',
        opacity: 1,
      }
    : {
        transform: CSS.Transform.toString(transform),
        transition: transition ? 'transform 120ms ease' : undefined,
        opacity: sortableDragging ? 0.4 : 1,
      }

  const isCompleted = todo.status === 'done'
  const prio = todo.priority

  const priorityRowStyle: React.CSSProperties | undefined =
    prio == null
      ? undefined
      : {
          borderLeft: `3px solid var(--color-prio-${prio})`,
          background: `var(--color-prio-${prio}-bg)`,
        }

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{
        ...style,
        cursor: sortableDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
      }}
      onKeyDown={handleDeleteKeyDown}
      tabIndex={0}
      onContextMenu={contextMenu.onContextMenu}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 4px',
          borderRadius: 'var(--radius-sm)',
          position: 'relative',
          ...priorityRowStyle,
        }}
        className='todo-item-row group'
      >
        <button
          onClick={handleStatusClick}
          onPointerDown={(e) => e.stopPropagation()}
          className={`status-circle ${todo.status === 'done' ? 'done' : ''}`}
          title={`Status: ${todo.status.replace('_', ' ')}. Click to cycle.`}
          style={{
            marginTop: '3px',
          }}
        />

        <div style={{ flex: 1 }}>
          {editing ? (
            <input
              ref={inputRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onBlur={handleEditSave}
              onKeyDown={handleEditKeyDown}
              onPointerDown={(e) => e.stopPropagation()}
              style={{
                width: '100%',
                background: 'transparent',
                border: 'none',
                borderBottom: '1px solid var(--color-accent)',
                outline: 'none',
                fontFamily: 'var(--font-serif)',
                fontSize: 'inherit',
                color: 'var(--color-ink)',
                padding: '0',
              }}
              autoFocus
            />
          ) : (
            <span
              onClick={handleTextClick}
              className='todo-item-text'
              style={{
                cursor: 'inherit',
                fontFamily: 'var(--font-serif)',
                fontSize: 'var(--text-body)',
                lineHeight: 1.55,
                fontWeight: 400,
                color: isCompleted ? 'var(--color-ink-faint)' : 'var(--color-ink)',
                textDecoration: 'none',
                display: 'block',
                whiteSpace: 'normal',
                overflowWrap: 'break-word',
              }}
            >
              {todo.text}
            </span>
          )}
        </div>

        <div
          className='todo-item-icons'
          style={{
            display: 'flex',
            gap: '4px',
            alignItems: 'center',
            flexShrink: 0,
            position: 'absolute',
            top: '2px',
            right: '2px',
            padding: '3px 5px',
            borderRadius: 'var(--radius-sm)',
            background: 'var(--color-surface)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            boxShadow: 'var(--shadow-sm)',
            opacity: 0,
            pointerEvents: 'none',
            transition: 'opacity 0.12s',
          }}
        >
          {isRecurring && (
            <span
              title="Recurring task"
              style={{
                color: 'var(--color-ink-faint)',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Repeat size={12} strokeWidth={1.5} />
            </span>
          )}
          <ReminderPicker todo={todo} />
          {todo.note ? (
            <button
              onClick={handleOpenNoteDrawer}
              onPointerDown={(e) => e.stopPropagation()}
              title='Open note'
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-ink-muted)',
                padding: '0 2px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <FileText size={12} strokeWidth={1.5} />
            </button>
          ) : (
            <button
              onClick={handleOpenNoteDrawer}
              onPointerDown={(e) => e.stopPropagation()}
              title='Add note'
              className='todo-note-add-btn'
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--color-ink-muted)',
                padding: '0 2px',
                display: 'flex',
                alignItems: 'center',
                transition: 'color 0.12s',
              }}
            >
              <FilePlus size={12} strokeWidth={1.5} />
            </button>
          )}
          <button
            onClick={handleDeleteClick}
            onPointerDown={(e) => e.stopPropagation()}
            title='Delete todo'
            className='todo-delete-btn'
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--color-ink-muted)',
              padding: '0 2px',
              display: 'flex',
              alignItems: 'center',
              transition: 'color 0.12s',
            }}
          >
            <Trash2 size={14} strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <ContextMenu open={contextMenu.open} position={contextMenu.position} onClose={contextMenu.closeMenu} items={contextMenuSections} />

      <RecurrencePicker
        open={recurrenceOpen}
        onClose={() => setRecurrenceOpen(false)}
        sourceTodo={todo}
      />

      <style>{`
        .todo-item-row:hover .todo-item-icons,
        .todo-item-row:focus-within .todo-item-icons {
          opacity: 1 !important;
          pointer-events: auto !important;
        }
        .todo-note-add-btn:hover {
          color: var(--color-accent) !important;
        }
        .todo-delete-btn:hover {
          color: #B43A2C !important;
        }
      `}</style>
    </div>
  );
}
